// [S-408] SetPassword — the page an invited user lands on to choose their
// password. Replaces Identity Platform's hosted reset page so we control the
// link TTL (6h) and render graceful expired / invalid / already-used states.
//
// The token rides in the query string. We only POST it (consuming it) when the
// user submits — so an email scanner GET-ing this page doesn't burn the invite.
// On success the user is sent to /login to sign in with their new password.
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, useApi } from "../data/api";
import { Button } from "../components";

const MIN_LEN = 8;

type FailCode =
  | "expired"
  | "used"
  | "invalid"
  | "weak_password"
  | "mismatch"
  | "network";

const FAIL_COPY: Record<FailCode, { title: string; body: string }> = {
  expired: {
    title: "This invite link has expired",
    body: "Invite links are valid for a limited time. Ask your admin to resend your invite.",
  },
  used: {
    title: "This invite link was already used",
    body: "If you didn't set your password, ask your admin to resend your invite.",
  },
  invalid: {
    title: "This invite link is invalid",
    body: "The link may be incomplete. Ask your admin to resend your invite.",
  },
  weak_password: {
    title: "Choose a stronger password",
    body: `Use at least ${MIN_LEN} characters.`,
  },
  mismatch: {
    title: "Passwords don't match",
    body: "Enter the same password in both fields.",
  },
  network: {
    title: "Something went wrong",
    body: "Please try again. If it keeps happening, ask your admin to resend your invite.",
  },
};

export function SetPassword(): JSX.Element {
  const [search] = useSearchParams();
  const token = search.get("token") ?? "";
  const api = useApi("admin");
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fail, setFail] = useState<FailCode | null>(token.length === 0 ? "invalid" : null);

  const blocked = fail === "expired" || fail === "used" || fail === "invalid";

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setFail(null);
    if (password.length < MIN_LEN) {
      setFail("weak_password");
      return;
    }
    if (password !== confirm) {
      setFail("mismatch");
      return;
    }
    setSubmitting(true);
    try {
      await api("/auth/set-password", {
        method: "POST",
        body: { token, password },
      });
      navigate("/login?welcome=1", { replace: true });
    } catch (err) {
      const code =
        err instanceof ApiError &&
        typeof (err.body as { code?: unknown })?.code === "string"
          ? ((err.body as { code: string }).code as FailCode)
          : "network";
      setFail(code in FAIL_COPY ? code : "network");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login" data-testid="set-password">
      <div className="login__card">
        <h1 className="login__title">Set your password</h1>

        {blocked ? (
          <div data-testid="set-password-blocked">
            <p className="login__sub">
              <strong>{FAIL_COPY[fail].title}.</strong> {FAIL_COPY[fail].body}
            </p>
            <div style={{ marginTop: 16 }}>
              <Link to="/login">
                <Button variant="bare">Back to sign-in</Button>
              </Link>
            </div>
          </div>
        ) : (
          <form className="login__form" onSubmit={(e) => void onSubmit(e)}>
            <p className="login__sub">
              Choose a password for your ContractStudio account.
            </p>
            <div className="login__field">
              <label htmlFor="sp-pw">New password</label>
              <input
                id="sp-pw"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFail(null);
                }}
                data-testid="set-password-input"
              />
            </div>
            <div className="login__field">
              <label htmlFor="sp-confirm">Confirm password</label>
              <input
                id="sp-confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setFail(null);
                }}
                data-testid="set-password-confirm"
              />
            </div>
            {/* A submitted error always surfaces (never a silent no-op); when
                nothing was submitted yet, a live hint flags an obvious mismatch
                as the user types. */}
            {fail !== null ? (
              <p className="login__error" role="alert" data-testid="set-password-error">
                {FAIL_COPY[fail].body}
              </p>
            ) : confirm.length > 0 && password !== confirm ? (
              <p className="login__error" role="alert" data-testid="set-password-error">
                {FAIL_COPY.mismatch.body}
              </p>
            ) : null}
            <div style={{ marginTop: 16 }}>
              <Button
                variant="primary"
                type="submit"
                disabled={submitting}
                data-testid="set-password-submit"
              >
                {submitting ? "Setting…" : "Set password"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
